import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Lock } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import AuthModal from './AuthModal';
import { chatSend, chatMessages, chatUnread } from '../api/chat';

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const ChatWidget = () => {
  const { isAuthenticated, user, authChecked } = useStore();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const wantOpenAfterLogin = useRef(false);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const loadMessages = useCallback(async () => {
    try {
      const d = await chatMessages();
      setMessages(d.messages || []);
      setUnread(0);
      scrollToBottom();
    } catch (e) { console.warn('[chat] failed to load messages:', e?.message || e); }
  }, []);

  // Badge polling when authed and panel closed
  useEffect(() => {
    if (!isAuthenticated) { setUnread(0); return; }
    let active = true;
    const tick = async () => {
      if (open) return;
      try { const d = await chatUnread(); if (active) setUnread(d.unread || 0); } catch (e) { console.warn('[chat] unread poll failed:', e?.message || e); }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { active = false; clearInterval(id); };
  }, [isAuthenticated, open]);

  // Thread polling while open
  useEffect(() => {
    if (open && isAuthenticated) {
      loadMessages();
      pollRef.current = setInterval(loadMessages, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [open, isAuthenticated, loadMessages]);

  const handleBubble = () => {
    if (!isAuthenticated) { wantOpenAfterLogin.current = true; setAuthOpen(true); return; }
    setOpen((o) => !o);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    // optimistic
    const optimistic = { id: `tmp-${Date.now()}`, sender: 'customer', text: t, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setText('');
    scrollToBottom();
    try {
      await chatSend(t);
      await loadMessages();
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setText(t);
    } finally {
      setSending(false);
    }
  };

  // Auto-open the chat right after a successful login that was triggered from the widget
  useEffect(() => {
    if (isAuthenticated && wantOpenAfterLogin.current) {
      wantOpenAfterLogin.current = false;
      setAuthOpen(false);
      setOpen(true);
    }
  }, [isAuthenticated]);

  if (!authChecked) return null;

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={handleBubble}
        data-testid="chat-widget-bubble"
        aria-label="Open support chat"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-[#22c55e] hover:bg-[#1ea34d] text-black shadow-xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
        {!open && unread > 0 && (
          <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && isAuthenticated && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          <div className="bg-black text-white px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="h-9 w-9 rounded-full bg-[#22c55e] flex items-center justify-center text-black"><MessageCircle size={18} /></div>
            <div className="min-w-0">
              <div className="font-bold text-sm leading-tight">Feron Support</div>
              <div className="text-[11px] text-gray-300">We usually reply within a few hours</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-neutral-50">
            {messages.length === 0 && (
              <div className="text-center text-gray-400 text-sm mt-8 px-6">
                Hi {user?.name?.split(' ')[0] || 'there'}! 👋 Ask us anything about your order, sizing, or products — we'll get back to you here.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === 'customer' ? 'bg-[#22c55e] text-black rounded-br-sm' : 'bg-white border text-gray-800 rounded-bl-sm'}`}>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[10px] mt-1 ${m.sender === 'customer' ? 'text-black/60' : 'text-gray-400'}`}>{fmtTime(m.created_at)}</div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSend} className="p-2.5 border-t bg-white flex items-center gap-2 shrink-0">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="chat-input"
              placeholder="Type your message..."
              className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#22c55e]"
            />
            <button type="submit" disabled={sending || !text.trim()} data-testid="chat-send-btn" className="h-10 w-10 rounded-full bg-[#22c55e] hover:bg-[#1ea34d] disabled:opacity-40 text-black flex items-center justify-center shrink-0">
              <Send size={17} />
            </button>
          </form>
        </div>
      )}

      {/* Login gate */}
      {authOpen && !isAuthenticated && (
        <div className="fixed bottom-24 right-5 z-[55] w-[92vw] max-w-[300px] bg-white rounded-xl shadow-2xl border p-4 text-center" data-testid="chat-login-hint">
          <Lock size={20} className="mx-auto text-[#22c55e] mb-2" />
          <p className="text-sm text-gray-700">Please sign in to chat with us — it lets us reply to you directly.</p>
        </div>
      )}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
};

export default ChatWidget;
